import "../Management/ContentManagement.css";
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LuNotepadText } from "react-icons/lu";
import { AddTask } from "./ContentManagement/AddTask";
import { PendingTaskSection } from "./ContentManagement/PendingTaskSection";
import { ApprovedTaskSection } from "./ContentManagement/ApprovedTaskSection";
import { LaunchedTaskSection } from "./ContentManagement/LaunchedTaskSection";

/**
 * Main task management component - handles authentication and coordinates task sections
 */
export function ContentManagement() {
  const navigate = useNavigate();
  
  // Core state
  const [companyProfile, setCompanyProfile] = useState(null); // Company data for authenticated user
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
  
  // UI visibility controls
  const [showMainHeader, setShowMainHeader] = useState(true); // Main header with title and buttons
  const [showAddTaskForm, setShowAddTaskForm] = useState(false); // Add task form visibility
  const [showPendingTasksSection, setShowPendingTasksSection] = useState(true); // Pending tasks section
  const [showApprovedTasksSection, setShowApprovedTasksSection] = useState(true); // Approved tasks section
  
  // Task detail view controls
  const [showPendingTaskDetail, setShowPendingTaskDetail] = useState(false); // Pending task detail view
  const [showApprovedTaskDetail, setShowApprovedTaskDetail] = useState(false); // Approved task detail view
  const [showLaunchedTasksWindow, setShowLaunchedTasksWindow] = useState(false); // Launched tasks window
  const [showLaunchedTaskDetail, setShowLaunchedTaskDetail] = useState(false); // Launched task detail view

  // Authentication and data initialization
  useEffect(() => {
    const validateTokenAndFetchData = async () => {
      try {
        // Check for auth token
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // Validate token
        const tokenResponse = await fetch(process.env.REACT_APP_API_URL + "/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!tokenResponse.ok) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }

        // Fetch company profile
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/company/get-company-profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data) {
          setCompanyProfile(response.data);
          localStorage.setItem("companyProfileId", response.data._id);
          console.log("ContentManagement: Stored companyProfileId in localStorage:", response.data._id);
        } else {
          toast.error("Company profile not found", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          navigate("/new-company");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        
        if (error.response?.status === 404) {
          toast.error("Please create a company profile first", {
            className: "gradient-toast",
            bodyClassName: "gradient-toast-body",
            progressClassName: "gradient-toast-progress",
          });
          navigate("/new-company");
        } else if (error.name === 'TypeError' || error.message.includes('token')) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateTokenAndFetchData();
  }, [navigate]);

  // Callback handlers
  const handleTaskCreated = () => {
    // Task creation handled by AddTask component
  };

  const handleCloseAddTask = () => {
    setShowAddTaskForm(false);
    setShowPendingTasksSection(true);
    setShowApprovedTasksSection(true);
    setShowApprovedTaskDetail(false);
  };

  // UI state handlers
  const handlePendingTaskStatusChange = (status) => {
    setShowMainHeader(!status);
    if (status === false) {
      setShowPendingTasksSection(true);
      setShowApprovedTasksSection(true);
      setShowPendingTaskDetail(false);
    } else {
      setShowPendingTasksSection(false);
      setShowApprovedTasksSection(false);
      setShowPendingTaskDetail(true);
    }
  };

  const handleApprovedTaskStatusChange = (status) => {
    setShowMainHeader(!status);
    setShowPendingTasksSection(!status);
    setShowApprovedTasksSection(!status);
    setShowApprovedTaskDetail(status);
  };

  const handleLaunchedTaskWindowToggle = () => {
    const newState = !showLaunchedTasksWindow;
    setShowLaunchedTasksWindow(newState);
    setShowPendingTasksSection(!newState);
    setShowApprovedTasksSection(!newState);
    setShowMainHeader(!newState);
    setShowLaunchedTaskDetail(false);
  };

  const handleLaunchedTaskDetailToggle = () => {
    const newState = !showLaunchedTaskDetail;
    setShowLaunchedTaskDetail(newState);
    setShowLaunchedTasksWindow(!newState);
  };

  const toggleAddTaskForm = () => {
    if (!showAddTaskForm) {
      setShowAddTaskForm(true);
      setShowPendingTasksSection(false);
      setShowApprovedTasksSection(false);
      setShowApprovedTaskDetail(false);
    } else {
      setShowPendingTasksSection(true);
      setShowApprovedTasksSection(true);
      setShowApprovedTaskDetail(false);
      setShowAddTaskForm(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="loader-container">
        <span className="loader"></span>
      </div>
    );
  }
  return (
    <div className="content-management-page">
      {/* Main header - title, add task button, launched reports button */}
      {showMainHeader && (
        <div className="task-title-and-new-task">
          <div className="task-title">Tasks</div>
          
          <div className="new-task-button" onClick={toggleAddTaskForm}>
            + Assign New Task
          </div>
          
          {/* Launched Reports Button */}
          <div className="approved-list-container">
            <span className="tooltiptext">Launched Reports</span>
            <div
              className="appproved-list-btn"
              onClick={handleLaunchedTaskWindowToggle}
            >
              <LuNotepadText
                style={{ height: "30px", width: "30px", color: "grey" }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Main task sections container */}
      <div className="task-row">
        <div className="task-boxes">
          
          {/* Add Task Form - creates new tasks */}
          {showAddTaskForm && (
            <AddTask
              companyProfile={companyProfile}
              onTaskCreated={handleTaskCreated}
              onClose={handleCloseAddTask}
            />
          )}
          
          {/* Pending Tasks - awaiting approval */}
          <PendingTaskSection
            companyProfile={companyProfile}
            showPendingTasksSection={showPendingTasksSection}
            showPendingTaskDetail={showPendingTaskDetail}
            onTaskStatusChange={handlePendingTaskStatusChange}
          />
        </div>
      </div>
      
      {/* Approved Tasks - ready for launch */}
      <ApprovedTaskSection
        companyProfile={companyProfile}
        showSecondHalf={showApprovedTasksSection}
        showCompletedTaskStatus={showApprovedTaskDetail}
        onTaskStatusChange={handleApprovedTaskStatusChange}
      />

      {/* Launched Tasks - completed tasks and reports */}
      <LaunchedTaskSection
        launchedTaskWindow={showLaunchedTasksWindow}
        showLaunchedTaskDetail={showLaunchedTaskDetail}
        companyProfile={companyProfile}
        onLaunchedTaskWindowToggle={handleLaunchedTaskWindowToggle}
        onLaunchedTaskDetailToggle={handleLaunchedTaskDetailToggle}
      />

      {/* Toast notifications */}
      <ToastContainer
        position="top-right"
        autoClose={500}
        hideProgressBar={false}
        newestOnTop={true}
        transition={Zoom}
      />
    </div>
  );
}
